import { TokenIcon } from '@ui/components/TokenIcon';
import { Text } from '@ui/lib/park/ui/text';
import { useTranslation } from 'next-i18next';
import { useContext, useMemo } from 'react';
import { Flex, HStack } from 'styled-system/jsx';
import { useReadContract } from 'wagmi';
import SLVMONABI from '@ui/services/contract/abis/SLVMON';
import { Address, formatUnits, parseUnits } from 'viem';
import BigNumber from 'bignumber.js';

import { ExchangeContext } from '../Context';

export const TextUnstakeFees = () => {
  const { t } = useTranslation();
  const { inputToken, outputToken, outputAmount, inputAmount } = useContext(ExchangeContext);

  // Convert inputAmount (Shares) to Assets
  const inputAmountBN = useMemo(() => {
    if (!inputAmount || !inputToken) return 0n;
    try {
      return parseUnits(inputAmount, inputToken.decimals);
    } catch {
      return 0n;
    }
  }, [inputAmount, inputToken]);

  const { data: inputAssets } = useReadContract({
    address: inputToken?.address as Address,
    abi: SLVMONABI,
    functionName: 'convertToAssets',
    args: [inputAmountBN],
    query: {
      enabled: !!inputToken?.address && inputAmountBN > 0n,
    },
  });

  const fees = useMemo(() => {
    if (!inputAssets || !outputAmount) return '0';
    
    // inputAssets is BigInt (wei)
    // outputAmount is string (ether/formatted)
    
    // We need to compare in the same unit.
    // outputToken is likely LVMON (18 decimals).
    
    const inputAssetsFormatted = new BigNumber(formatUnits(inputAssets, outputToken?.decimals || 18));
    const outputAmountBN = new BigNumber(outputAmount.replace(/,/g, ''));
    
    const fee = inputAssetsFormatted.minus(outputAmountBN);
    
    if (fee.lt(0)) return '0'; // Should not happen ideally, but safety check
    
    return fee.decimalPlaces(6).toFormat();
  }, [inputAssets, outputAmount, outputToken]);

  return (
    <Flex width="full" justifyContent="space-between" alignItems="center" py={1}>
      <Text color="greyWhite.white" fontWeight="light" fontSize="14px">
        {t("common.Fees:")}
      </Text>
      <HStack gap={1}>
        <Text fontSize="14px" fontWeight="normal" color="white">
          {fees} {outputToken?.symbol}
        </Text>
      </HStack>
    </Flex>
  );
};
